import { useEffect, useState } from 'react'
import { useRxDB } from '../core/useRxDB'
import type {
  PlanCode,
  PlanDocument,
  SubscriptionDocument,
  SubscriptionInfo,
  UsageDocument,
  UsageLimitCheckResult,
  UsageLimitKey,
} from '@/lib/types/subscription'
import { API_URL } from '@/lib/config'
import useLoginStore from '@/store/LoginStore'
import { getAccessToken } from '@/lib/auth/authSession'
import {
  canDowngrade,
  canUpgrade,
  checkUsageLimit,
  getNextTier,
  getPreviousTier,
  getTrialDaysLeft,
  isCanceled,
  isExpired,
  isPastDue,
  isSubscriptionActive,
  isTrialing,
} from '@/lib/types/subscriptionHelpers'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

/**
 * Hook to get current environment's subscription
 * Returns null if no subscription found
 */
export function useCurrentSubscription(): SubscriptionDocument | null {
  const { db } = useRxDB()
  // envId maps to workspaceId from LoginStore
  const envId = useLoginStore((state: any) => state.workspaceId)
  const [subscription, setSubscription] = useState<SubscriptionDocument | null>(
    null,
  )

  useEffect(() => {
    if (!db || !envId) {
      setSubscription(null)
      return
    }
    const sub = db.subscriptions
      .findOne({
        selector: { envId, isDeleted: false },
      })
      .$.subscribe((doc) =>
        setSubscription(doc ? doc.toMutableJSON() : null),
      )
    
    return () => sub.unsubscribe()
  }, [db, envId])

  return subscription
}

/**
 * Hook to get all public plans
 * Sorted by sortOrder
 */
export function usePlans(): Array<PlanDocument> {
  const { db } = useRxDB()
  const [plans, setPlans] = useState<Array<PlanDocument>>([])

  useEffect(() => {
    if (!db) {
      setPlans([])
      return
    }

    const sub = db.plans
      .find({
        selector: { isPublic: true, isDeleted: false },
        sort: [{ sortOrder: 'asc' }],
      })
      .$.subscribe((docs) =>
        setPlans(docs.map((doc) => doc.toMutableJSON())),
      )

    return () => sub.unsubscribe()
  }, [db])

  return plans
}

/**
 * Hook to get a specific plan by code
 */
export function usePlan(code: PlanCode): PlanDocument | null {
  const { db } = useRxDB()
  const [plan, setPlan] = useState<PlanDocument | null>(null)

  useEffect(() => {
    if (!db) {
      setPlan(null)
      return
    }

    const sub = db.plans
      .findOne({
        selector: { code, isDeleted: false },
      })
      .$.subscribe((doc) => setPlan(doc ? doc.toMutableJSON() : null))

    return () => sub.unsubscribe()
  }, [db, code])

  return plan
}

/**
 * Hook to get current plan (from subscription)
 */
export function useCurrentPlan(): PlanDocument | null {
  const { db } = useRxDB()
  const subscription = useCurrentSubscription()
  const [plan, setPlan] = useState<PlanDocument | null>(null)

  useEffect(() => {
    if (!db || !subscription) {
      setPlan(null)
      return
    }

    const sub = db.plans
      .findOne({
        selector: { id: subscription.planId },
      })
      .$.subscribe((doc) => setPlan(doc ? doc.toMutableJSON() : null))

    return () => sub.unsubscribe()
  }, [db, subscription])

  return plan
}

/**
 * Hook to get current usage metrics
 */
export function useCurrentUsage(): UsageDocument | null {
  const { db } = useRxDB()
  // envId maps to workspaceId from LoginStore
  const envId = useLoginStore((state: any) => state.workspaceId)
  const [usage, setUsage] = useState<UsageDocument | null>(null)

  useEffect(() => {
    if (!db || !envId) {
      setUsage(null)
      return
    }

    const sub = db.usage
      .findOne({
        selector: { envId },
      })
      .$.subscribe((doc) => setUsage(doc ? doc.toMutableJSON() : null))

    return () => sub.unsubscribe()
  }, [db, envId])

  return usage
}

/**
 * Comprehensive hook that combines all subscription data
 * with computed properties
 */
export function useSubscriptionInfo(): SubscriptionInfo {
  const subscription = useCurrentSubscription()
  const plan = useCurrentPlan()
  const usage = useCurrentUsage()
  const envId = useLoginStore((state: any) => state.workspaceId)

  // Fallback subscription fetched directly from API sync when local RxDB hasn't synced yet
  const [fallbackSub, setFallbackSub] = useState<SubscriptionDocument | null>(
    null,
  )

  useEffect(() => {
    if (!FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS) {
      return
    }
    if (subscription || !envId) {
      setFallbackSub(null)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const baseUrl = API_URL
        const resp = await fetch(
          `${baseUrl}/api/sync/subscriptions/pull?` +
            new URLSearchParams({
              env: envId,
              updatedAt: '0',
              limit: '1',
            }),
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
              'Content-Type': 'application/json',
            },
          },
        )
        if (!resp.ok) return
        const data = await resp.json()
        const doc = data?.documents?.[0]
        if (!doc || cancelled) return

        const now = Date.now()
        setFallbackSub({
          id: doc.id,
          envId,
          planId: doc.planId,
          status: doc.status,
          trialStartDate: doc.trialStartDate ?? null,
          trialEndDate: doc.trialEndDate ?? null,
          currentPeriodStart: doc.currentPeriodStart ?? now,
          currentPeriodEnd: doc.currentPeriodEnd ?? now,
          billingInterval: (doc.billingInterval as any) ?? 'monthly',
          canceledAt: doc.canceledAt ?? null,
          cancelAtPeriodEnd: doc.cancelAtPeriodEnd ?? false,
          stripeCustomerId: doc.stripeCustomerId ?? null,
          stripeSubscriptionId: doc.stripeSubscriptionId ?? null,
          paddleCustomerId: doc.paddleCustomerId ?? null,
          paddleSubscriptionId: doc.paddleSubscriptionId ?? null,
          billingProvider: doc.billingProvider ?? 'stripe',
          billingEmail: doc.billingEmail ?? '',
          isDeleted: doc.isDeleted ?? false,
          createdOn: doc.createdOn ?? now,
          createdBy: doc.createdBy ?? '',
          updatedOn: doc.updatedOn ?? now,
          updatedBy: doc.updatedBy ?? '',
          deletedOn: doc.deletedOn ?? null,
          deletedBy: doc.deletedBy ?? null,
        })
      } catch {
        // Ignore fallback errors
      }
    })()

    return () => {
      cancelled = true
    }
  }, [envId, subscription])

  const effectiveSub = subscription ?? fallbackSub

  const isActive = isSubscriptionActive(effectiveSub)
  const isInTrial = isTrialing(effectiveSub)
  const trialDaysLeft = getTrialDaysLeft(effectiveSub)
  const expired = isExpired(effectiveSub)
  const pastDue = isPastDue(effectiveSub)
  const canceled = isCanceled(effectiveSub)

  const currentTier = plan?.code ?? null
  const canUpgradePlan = currentTier ? canUpgrade(currentTier) : false
  const canDowngradePlan = currentTier ? canDowngrade(currentTier) : false
  const nextTier = currentTier ? getNextTier(currentTier) : null
  const previousTier = currentTier ? getPreviousTier(currentTier) : null

  return {
    subscription: effectiveSub,
    plan,
    usage,
    isTrialing: isInTrial,
    trialDaysLeft,
    isExpired: expired,
    isActive,
    isPastDue: pastDue,
    isCanceled: canceled,
    canUpgrade: canUpgradePlan,
    canDowngrade: canDowngradePlan,
    currentTier,
    nextTier,
    previousTier,
  }
}

/**
 * Hook to check usage limit
 * Returns full check result with usage details
 */
export function useUsageLimitCheck(
  limit: UsageLimitKey,
): UsageLimitCheckResult {
  const plan = useCurrentPlan()
  const usage = useCurrentUsage()
  return checkUsageLimit(plan, usage, limit)
}

/**
 * Hook to check if over a usage limit (simple boolean)
 */
export function useIsOverLimit(limit: UsageLimitKey): boolean {
  const result = useUsageLimitCheck(limit)
  return result.isOverLimit
}
